import { useEffect, useMemo, useState } from "react";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { LayoutGrid, Check, Plus, RotateCcw } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { WidgetFrame } from "./WidgetFrame.js";
import { useDashboardLayout } from "./useDashboardLayout.js";
import { DashboardGridSkeleton } from "./widgets/widget-skeletons.js";
import {
  resolveLayout, toLayoutEntries,
  type DashboardWidgetDef, type ResolvedWidget, type WidgetProps, type WidgetSize,
} from "./widget-registry.js";

/**
 * Arranges the dashboard's widgets and hosts edit mode.
 *
 * It depends on the widget *contract*, never on a concrete widget module
 * (dependency inversion): the registry is passed in, so this same grid can drive
 * a manager or department dashboard by handing it a different list.
 */
export interface DashboardGridProps {
  registry: DashboardWidgetDef[];
  ctx: WidgetProps;
  /**
   * Edit mode is controlled by the page, because its toggle lives in the page
   * header (top right) rather than above the grid. The grid still owns every
   * other edit affordance — add, reset, drag handles, size pickers.
   */
  editing: boolean;
}

/** The Customise / Done toggle. Rendered by the page, in its header. */
export function DashboardEditToggle({ editing, onToggle }: { editing: boolean; onToggle: () => void }) {
  return (
    <Button type="button" variant={editing ? "default" : "secondary"} size="sm" className="max-sm:hidden" onClick={onToggle}>
      {editing
        ? <><Check size={14} className="mr-1.5" />Done</>
        : <><LayoutGrid size={14} className="mr-1.5" />Customise</>}
    </Button>
  );
}

export function DashboardGrid({ registry, ctx, editing }: DashboardGridProps) {
  const { saved, loading, saveError, save, reset } = useDashboardLayout();
  const [adding, setAdding] = useState(false);

  // Leaving edit mode closes the add panel, so re-entering starts clean.
  useEffect(() => { if (!editing) setAdding(false); }, [editing]);

  // Registry (what exists) + saved layout (order/size/visibility) → what renders.
  const widgets = useMemo(() => resolveLayout(registry, saved, ctx), [registry, saved, ctx]);

  const visible = widgets.filter((w) => !w.isHidden);
  const hidden  = widgets.filter((w) => w.isHidden);

  const sensors = useSensors(
    // A small distance threshold means a click on a widget still behaves like a
    // click; only a deliberate drag starts a move.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** Persists a changed arrangement; the store debounces the write. */
  const commit = (next: ResolvedWidget[]) => save(toLayoutEntries(next));

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;

    // Reorder within the visible list, then append the hidden ones so their
    // stored rows survive the save untouched.
    const from = visible.findIndex((w) => w.def.key === active.id);
    const to   = visible.findIndex((w) => w.def.key === over.id);
    if (from < 0 || to < 0) return;

    commit([...arrayMove(visible, from, to), ...hidden]);
  }

  const setSize = (key: string, size: WidgetSize) =>
    commit(widgets.map((w) => (w.def.key === key ? { ...w, size } : w)));

  const setHidden = (key: string, isHidden: boolean) =>
    commit(widgets.map((w) => (w.def.key === key ? { ...w, isHidden } : w)));

  if (loading) return <DashboardGridSkeleton />;

  return (
    <>
      {editing && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setAdding((v) => !v)}>
            <Plus size={14} className="mr-1.5" />
            Add widget{hidden.length > 0 && ` (${hidden.length})`}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => { void reset(); setAdding(false); }}>
            <RotateCcw size={14} className="mr-1.5" />
            Reset to default
          </Button>
          <p className="text-[11px] text-surface-500">
            Drag a card by its title to reorder, or set its width. Changes save automatically.
          </p>
        </div>
      )}

      {saveError && (
        <Card className="mb-4 border-[var(--color-error-border)] bg-[var(--color-error-bg-subtle)] p-3">
          <p className="text-[12px] text-[var(--color-error-fg)]">{saveError}</p>
        </Card>
      )}

      {editing && adding && (
        <Card className="mb-4 p-4">
          <h3 className="mb-3 text-[13px] font-semibold">Hidden widgets</h3>
          {hidden.length === 0 ? (
            <p className="text-[12px] text-surface-400">Every widget is already on your dashboard.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {hidden.map((w) => (
                <li key={w.def.key}>
                  <Button type="button" variant="outline" size="sm" onClick={() => setHidden(w.def.key, false)}>
                    <Plus size={14} className="mr-1.5" />
                    {w.def.title}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visible.map((w) => w.def.key)} strategy={rectSortingStrategy}>
          {/* 6 tracks, so sm/md/lg/full map to 2/3/4/6 spans -- a third, a half,
              two-thirds and full. Single column on phones, where every widget is
              full width whatever its size. */}
          <div className={cn("grid grid-cols-1 items-start gap-4 md:grid-cols-6", editing && "gap-3")}>
            {visible.map((w) => {
              const Widget = w.def.component;
              return (
                <WidgetFrame
                  key={w.def.key}
                  def={w.def}
                  size={w.size}
                  editing={editing}
                  onSizeChange={(s) => setSize(w.def.key, s)}
                  onHide={() => setHidden(w.def.key, true)}
                >
                  <Widget {...ctx} />
                </WidgetFrame>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {visible.length === 0 && (
        <Card className="p-6 text-center">
          <p className="text-[13px] text-surface-500">
            Your dashboard is empty. Choose <strong>Customise</strong> at the top right to add a widget.
          </p>
        </Card>
      )}
    </>
  );
}
